import { useId } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Language } from '@dorixona/i18n'
import { Press, cx } from '@dorixona/ui'
import { formatDay, isoString } from '@dorixona/utils'
import { resolve, type NewsItem } from '../model'
import { NewsPhoto } from './NewsPhoto'

export interface NewsCardProps {
  item: NewsItem
  language: Language
  /** Карточка ленты — во всю колонку; без флага — карточка полки главной. */
  wide?: boolean
}

/**
 * Карточка новости. На полке главной — только фото и заголовок: полка
 * зазывает, а не пересказывает. В ленте «Все новости» к ним добавляются дата
 * и анонс по две строки. Вся карточка — одна цель нажатия и ведёт на экран
 * новости.
 *
 * Имя кнопки — заголовок, дата и анонс — её описание: диктор сначала говорит,
 * о чём новость, а голосовое управление находит кнопку по видимому заголовку
 * (accessibility.md › Mobility, Voice Control).
 */
export function NewsCard({ item, language, wide = false }: NewsCardProps) {
  const navigate = useNavigate()
  const id = useId()
  const title = resolve(item.title, language)
  const lead = resolve(item.lead, language)
  const dated = wide && item.publishedAt > 0
  const leadShown = wide && lead.text !== ''
  const described = [dated ? `${id}d` : '', leadShown ? `${id}l` : ''].filter(Boolean).join(' ')

  return (
    <Press
      squish="card"
      className={cx('news-card', wide && 'news-card--wide')}
      aria-labelledby={`${id}t`}
      aria-describedby={described || undefined}
      onPress={() => {
        void navigate(`/news/${item.id}`)
      }}
    >
      <NewsPhoto src={item.photo} />
      <span className="news-card__text">
        {dated ? (
          <time id={`${id}d`} className="news-card__date" dateTime={isoString(item.publishedAt)}>
            {formatDay(item.publishedAt)}
          </time>
        ) : null}
        <span id={`${id}t`} className="news-card__title" lang={title.lang}>
          {title.text}
        </span>
        {leadShown ? (
          <span id={`${id}l`} className="news-card__lead" lang={lead.lang}>
            {lead.text}
          </span>
        ) : null}
      </span>
    </Press>
  )
}

/** Заготовка карточки той же формы: экран не прыгает, когда новости приходят. */
export function NewsCardSkeleton({ wide = false }: { wide?: boolean }) {
  return (
    <span className={cx('news-card', 'news-card--sk', wide && 'news-card--wide')}>
      <i className="sk-b news-photo" />
      <span className="news-card__text">
        {wide ? <i className="sk-b sk-l sk-s" /> : null}
        <i className="sk-b sk-l" />
        <i className="sk-b sk-l" />
      </span>
    </span>
  )
}
